import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { logException } from '@atlaskit/editor-common/monitoring';
import { OVERFLOW_EXTENSIONS_MENU_SECTION } from '@atlaskit/editor-common/toolbar';
import type { RegisterComponent } from '@atlaskit/editor-toolbar-model/types';
import { ToolbarDropdownItem } from '@atlaskit/editor-toolbar/toolbar-dropdown-item';
import TemplateIcon from '@atlaskit/icon-lab/core/template';

import type { CreateSnippetRequest, SnippetsPlugin } from '../../snippetsPluginType';

const messages = defineMessages({
	createBlockTemplate: {
		id: 'editor-plugin-snippets.toolbar.getCreateSnippetToolbarComponents.createBlockTemplate',
		defaultMessage: 'Create block template',
		description: 'Label for creating a block template from the selected editor content',
	},
});

export const getCreateSnippetRequest = (
	adf?: ADFEntity,
	origin: CreateSnippetRequest['origin'] = 'contextualToolbar',
): CreateSnippetRequest | undefined => {
	if (!adf?.content?.length) {
		return undefined;
	}

	return { adf, origin };
};

type CreateSnippetToolbarItemProps = {
	api: Parameters<SnippetsPlugin>[0]['api'];
	onCreateSnippet: NonNullable<Parameters<SnippetsPlugin>[0]['config']['onCreateSnippet']>;
};

const createSnippetFromSelection = ({
	api,
	onCreateSnippet,
}: CreateSnippetToolbarItemProps): void => {
	const adf = api?.selectionExtension?.actions.getDocumentFromSelection?.()?.selectedNodeAdf;
	const request = getCreateSnippetRequest(adf);
	if (!request) {
		return;
	}

	try {
		onCreateSnippet(request);
	} catch (error) {
		logException(error as Error, {
			location: 'editor-plugin-snippets/createSnippetToolbarButton',
		});
	}
};

const CreateSnippetOverflowMenuItem = ({
	api,
	onCreateSnippet,
}: CreateSnippetToolbarItemProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const onClick = useCallback(
		() => createSnippetFromSelection({ api, onCreateSnippet }),
		[api, onCreateSnippet],
	);

	return (
		<ToolbarDropdownItem elemBefore={<TemplateIcon label="" size="small" />} onClick={onClick}>
			{formatMessage(messages.createBlockTemplate)}
		</ToolbarDropdownItem>
	);
};

export const getCreateSnippetToolbarComponents = ({
	api,
	onCreateSnippet,
}: CreateSnippetToolbarItemProps): RegisterComponent[] => [
	{
		type: 'menu-item',
		key: 'create-snippet-overflow-menu-item',
		parents: [
			{
				type: OVERFLOW_EXTENSIONS_MENU_SECTION.type,
				key: OVERFLOW_EXTENSIONS_MENU_SECTION.key,
				rank: 200,
			},
		],
		component: () => <CreateSnippetOverflowMenuItem api={api} onCreateSnippet={onCreateSnippet} />,
	},
];
